import { type RiskGapModel, riskGapDefaultSubstatuses } from '@ctw/shared/api/fhir/models/risk-gap';
import { Badge } from '@ctw/shared/components/badge';
import type { LegacyTableColumn } from '@ctw/shared/components/tables/legacy-table';
import { riskGapStatusBadgeDefinition } from '@ctw/shared/content/risk/risk-gap-status-badge-definition';
import type { useBuilderConfig } from '@ctw/shared/context/builder-config-context';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import { tw } from '@ctw/shared/utils/tailwind';
import { isEmpty, startCase } from 'lodash-es';

interface BuildPatientRiskGapsColumnsArgs {
  builderConfig: ReturnType<typeof useBuilderConfig>;
}

export const buildPatientRiskGapsColumns = ({
  builderConfig,
}: BuildPatientRiskGapsColumnsArgs): Array<LegacyTableColumn<RiskGapModel>> => [
  {
    title: 'Condition',
    minWidth: '30%',
    render: (model) => (
      <div className={tw`flex flex-col`}>
        <div className={tw`text-sm`}>{model.title}</div>
        {model.lastDiagnosedDisplay && (
          <div className={tw`text-content-subtle text-sm`}>
            Last Dx {model.lastDiagnosedDisplay}
          </div>
        )}
      </div>
    ),
  },
  {
    title: 'Diagnosis Type',
    render: (model) => {
      const diagnosisType = model.type === 'net-new' ? 'Newly Diagnosed' : startCase(model.type);
      return (
        <div className={tw`flex flex-col`}>
          <div className={tw`overflow-hidden text-ellipsis whitespace-nowrap text-sm`}>
            {diagnosisType}
          </div>
          {model.gapSourceListDisplay && (
            <div className={tw`text-content-subtle text-sm`}>{model.gapSourceListDisplay}</div>
          )}
        </div>
      );
    },
  },
  {
    title: 'Status',
    render: (model) => (
      <div className={tw`flex flex-col gap-1`}>
        <div className={tw`flex flex-wrap gap-1.5`}>
          <Badge {...riskGapStatusBadgeDefinition(model)} />
          {model.substatuses.map((substatus) => (
            <Badge
              key={substatus}
              variant="meta"
              content={
                (
                  builderConfig?.riskGaps?.statusOptions?.[model.status]?.substatusOptions ??
                  riskGapDefaultSubstatuses[model.status]
                ).find((option) => option.value === substatus)?.label ?? startCase(substatus)
              }
            />
          ))}
        </div>
        {!isEmpty(model.lastStatusUpdate.note) && (
          <div className={tw`text-content-subtle text-xs`}>{model.lastStatusUpdate.note}</div>
        )}
        <div className={tw`text-content-subtle text-xs`}>
          {formatFHIRDate(model.lastStatusUpdateDate)} by {model.lastUpdatedByDisplay}
        </div>
      </div>
    ),
  },
];
